import type { Config } from 'hb-builder/types';
import type { AuthenticationIds } from 'hb-builder/types/config/AuthenticationIds';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

const date = new Date().toISOString();

const expandDocument = ({
  authIds,
  document,
  type,
}: {
  authIds: Omit<AuthenticationIds, 'admin'>;
  document: DocumentItem;
  type: 'download' | 'upload';
}) => ({
  ...document,
  attributes: {
    ...document.attributes,
    loadHistory: [
      ...(document.attributes?.loadHistory || []),
      { authIds, type, date },
    ].filter(Boolean),
  },
});

export const downloadConfig = ({ config }: { config: Config }) => {
  const stringifiedDocument = JSON.stringify(config, null, 2);
  const blob = new Blob([stringifiedDocument], {
    type: 'text/plain;charset=utf-8',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.style.display = 'none';
  a.href = url;

  a.download = `network_config_${date}.txt`;
  document.body.appendChild(a);

  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const downloadDocument = ({
  authIds,
  fullDocument,
  name,
}: {
  authIds: AuthenticationIds;
  fullDocument: DocumentItem;
  name: string;
}) => {
  const { admin, ...rest } = authIds;
  const expandedDocument = expandDocument({
    authIds: rest,
    document: fullDocument,
    type: 'download',
  });
  const stringifiedDocument = JSON.stringify(expandedDocument, null, 2);
  const blob = new Blob([stringifiedDocument], {
    type: 'text/plain;charset=utf-8',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.style.display = 'none';
  a.href = url;

  a.download = `${name}_${date}.txt`;
  document.body.appendChild(a);

  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const upload = ({
  authIds,
  file,
  callback,
}: {
  authIds: AuthenticationIds;
  file: File;
  callback: (document: DocumentItem) => void;
}) => {
  const fr = new FileReader();

  fr.onload = function () {
    let JSONDocument;

    try {
      const tempDocument: DocumentItem =
        typeof fr.result === 'string' && JSON.parse(fr.result);

      const expandedDocument = expandDocument({
        authIds,
        document: tempDocument,
        type: 'upload',
      });

      JSONDocument = expandedDocument;
    } catch {
      JSONDocument = fr.result;
    }

    callback(JSONDocument);
  };

  fr.readAsText(file);
};
